// Per-star PLANETARY SYSTEM generation. Every campaign node is a real UMa3 cluster star; clicking it zooms
// into its system. Planets are imagined but astronomically CONSISTENT: stellar radius from Stefan-Boltzmann,
// habitable zone + snow line scaled by luminosity, orbital distances (AU) Kepler-spaced, equilibrium temps
// and periods derived, radii/masses/types physically plausible — and tuned for what an ANCIENT, METAL-POOR
// cluster ([Fe/H]≈-2, ~11 Gyr) would actually host: giant-planet formation is suppressed at low metallicity,
// so these are small, iron-poor, rocky/ice worlds — no gas giants. Deterministic in (star, seed).

import { mulberry32, type ClusterStar } from "./uma3";

export type PlanetType = "SUB-EARTH" | "ROCKY" | "TERRAN" | "SUPER-EARTH" | "MINI-NEPTUNE" | "ICE WORLD";
export type Zone = "hot" | "temperate" | "cold";

export interface Planet {
  name: string;        // "<system> b", "<system> c", ... by increasing distance
  aAU: number;         // semi-major axis (AU)
  radiusEarth: number; // planet radius in Earth radii
  massEarth: number;   // rough mass (Earth masses)
  tempK: number;       // equilibrium temperature
  periodDays: number;  // orbital period
  type: PlanetType;
  zone: Zone;
  inHZ: boolean;       // within the conservative habitable zone
  garden: boolean;     // in HZ AND rocky/terran sized — a candidate "garden world"
}

export interface StarSystem {
  name: string;
  tempK: number;
  lum: number;              // L / Lsun
  starRadiusSolar: number;  // R / Rsun
  starMassSolar: number;    // M / Msun
  hzInner: number;          // AU
  hzOuter: number;          // AU
  snowLine: number;         // AU
  planets: Planet[];        // sorted by increasing aAU
  gardenCount: number;
  note: string;
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/** Build a deterministic system for a cluster star. seedInt should fold the campaign seed + node identity. */
export function generateSystem(star: ClusterStar, seedInt: number, name: string): StarSystem {
  const rng = mulberry32(seedInt >>> 0);
  const L = star.lum;
  const T = star.tempK;

  // --- stellar params ---
  // Stefan-Boltzmann: R/Rsun = sqrt(L) * (Tsun/T)^2
  const starRadiusSolar = +(Math.sqrt(L) * Math.pow(5772 / T, 2)).toFixed(3);
  // old metal-poor cluster: low masses (turnoff ~0.8 Msun). Giants are evolved ~0.8 Msun stars.
  const starMassSolar = +clamp(0.10, 0.85, star.giant ? 0.80 : 0.10 + 0.75 * ((T - 2900) / 3400)).toFixed(3);

  // --- zones (AU), scaled by luminosity ---
  const hzInner = +Math.sqrt(L / 1.10).toFixed(3); // conservative inner (runaway greenhouse)
  const hzOuter = +Math.sqrt(L / 0.53).toFixed(3); // conservative outer (maximum greenhouse)
  const snowLine = +(2.7 * Math.sqrt(L)).toFixed(3);

  // --- planet count: metal-poor -> modest, small worlds; every system hosts at least one ---
  const r0 = rng();
  const n = r0 < 0.10 ? 1 : r0 < 0.34 ? 2 : r0 < 0.64 ? 3 : r0 < 0.87 ? 4 : 5;

  const planets: Planet[] = [];
  // innermost orbit anchored just inside the HZ, then Kepler-spaced outward
  let a = clamp(0.018, hzInner * 0.45, hzInner * (0.20 + rng() * 0.45));
  const letters = "bcdefghij";
  for (let i = 0; i < n; i++) {
    // radius: metal-poor -> small, iron-poor cores; occasional mini-Neptune beyond the snow line, NEVER a giant
    let radiusEarth: number;
    const beyondSnow = a > snowLine;
    if (beyondSnow && rng() < 0.22) radiusEarth = 1.9 + rng() * 1.4;      // rare small ice/mini-Neptune (<= 3.3 R⊕)
    else radiusEarth = 0.35 + Math.pow(rng(), 1.4) * 1.55;                // biased sub-/near-Earth (0.35–1.9 R⊕)
    radiusEarth = +radiusEarth.toFixed(2);

    // rocky worlds ~ R^3.7 density law; puffier (icy/volatile) worlds less dense
    const massEarth = +(radiusEarth < 1.6 ? Math.pow(radiusEarth, 3.7) : Math.pow(radiusEarth, 2.06) * 1.9).toFixed(2);

    // equilibrium temperature (albedo ~0.3): 255K * L^1/4 / sqrt(a)
    const tempK = Math.round(255 * Math.pow(L, 0.25) / Math.sqrt(a));
    const periodDays = +(365.25 * Math.sqrt((a * a * a) / starMassSolar)).toFixed(1);

    const inHZ = a >= hzInner && a <= hzOuter;
    const zone: Zone = tempK > 320 ? "hot" : tempK < 200 ? "cold" : "temperate";
    const type: PlanetType =
      radiusEarth >= 1.9 ? "MINI-NEPTUNE"
      : radiusEarth >= 1.55 ? "SUPER-EARTH"
      : zone === "cold" && radiusEarth < 1.55 ? "ICE WORLD"
      : radiusEarth < 0.7 ? "SUB-EARTH"
      : radiusEarth < 1.15 ? "ROCKY"
      : "TERRAN";
    const garden = inHZ && (type === "ROCKY" || type === "TERRAN" || type === "SUPER-EARTH");

    planets.push({
      name: `${name} ${letters[i]}`, aAU: +a.toFixed(3), radiusEarth, massEarth,
      tempK, periodDays, type, zone, inHZ, garden,
    });
    a *= 1.42 + rng() * 0.75; // Kepler-like spacing ratio ~1.4–2.2
  }

  const gardenCount = planets.filter((p) => p.garden).length;
  const note = gardenCount
    ? `[Fe/H]≈-2 · iron-poor rocky worlds · ${gardenCount} in the temperate belt`
    : `[Fe/H]≈-2 · metal-poor: small rocky/ice worlds, no gas giants`;

  return {
    name, tempK: T, lum: L, starRadiusSolar, starMassSolar,
    hzInner, hzOuter, snowLine, planets, gardenCount, note,
  };
}
